/**
 * Public legal pages: Privacy Policy, Terms of Service, Data Deletion.
 * Required URLs for Meta (Facebook + Instagram) app review and consumer trust.
 *
 * Routes:
 *   /privacy        - Privacy Policy
 *   /terms          - Terms of Service
 *   /data-deletion  - Data Deletion Instructions (Meta-required)
 *
 * If you change the company name or contact email, update both the constants
 * and the meta data-deletion callback in /admin → Platform Credentials.
 */
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

// ============================================================
// Shared constants — single source of truth
// ============================================================
const COMPANY = 'PostToSocial';
const SUPPORT_EMAIL = 'support@posttosocial.com';
const PRIVACY_EMAIL = 'privacy@posttosocial.com';
const DOMAIN = 'posttosocial.com';
const EFFECTIVE_DATE = 'February 2026';

// ============================================================
// Layout
// ============================================================
const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-zinc-50">
    {/* Header */}
    <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="legal-back-home">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-zinc-900">{COMPANY}</span>
        </Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/privacy" className="text-zinc-600 hover:text-indigo-600">Privacy</Link>
          <Link to="/terms" className="text-zinc-600 hover:text-indigo-600">Terms</Link>
          <Link to="/data-deletion" className="text-zinc-600 hover:text-indigo-600">Data Deletion</Link>
          <Link to="/login" className="text-zinc-600 hover:text-indigo-600">Sign in</Link>
        </nav>
      </div>
    </header>

    {/* Body */}
    <main className="max-w-4xl mx-auto px-6 py-12">
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 md:p-12">
        <h1 className="text-3xl md:text-4xl font-bold text-zinc-900 tracking-tight mb-2" data-testid="legal-title">
          {title}
        </h1>
        <p className="text-sm text-zinc-500 mb-8">Effective: {EFFECTIVE_DATE}</p>
        <article className="prose prose-zinc max-w-none prose-headings:font-semibold prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2 prose-p:text-zinc-700 prose-p:leading-relaxed prose-li:text-zinc-700 prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline">
          {children}
        </article>
      </div>
      <p className="text-center text-xs text-zinc-500 mt-8">
        Questions? Contact us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:underline">{SUPPORT_EMAIL}</a>
      </p>
    </main>
  </div>
);

// ============================================================
// Privacy Policy
// ============================================================
export const PrivacyPolicyPage = () => (
  <LegalLayout title="Privacy Policy">
    <p>
      This Privacy Policy describes how {COMPANY} ("we", "us", "our") collects, uses, and protects information
      when you use our service at <strong>{DOMAIN}</strong> (the "Service"). By using the Service, you agree to
      the practices described below.
    </p>

    <h2>1. Information We Collect</h2>

    <h3>1.1 Account information</h3>
    <ul>
      <li>Email address, name, and securely-hashed password (bcrypt) when you register</li>
      <li>Optional profile information (organization name, role) you provide</li>
      <li>Subscription tier, billing status, and payment history (processed by Stripe; we never store full card numbers)</li>
    </ul>

    <h3>1.2 Connected social platform data</h3>
    <p>
      When you connect a social account (LinkedIn, Facebook, Instagram, YouTube, TikTok, or X), we receive and store:
    </p>
    <ul>
      <li><strong>OAuth access tokens</strong> and refresh tokens issued by the platform — used solely to publish content you create on your behalf</li>
      <li><strong>Account display name</strong> (for example, your LinkedIn name or Facebook Page name) so we can show you which accounts are connected</li>
      <li><strong>Token expiration timestamp</strong> so we can refresh proactively</li>
    </ul>
    <p>
      We do <strong>not</strong> read your social inbox, friend list, follower data, or private messages.
      We do <strong>not</strong> sell or share your tokens with third parties.
    </p>

    <h3>1.3 Content you create</h3>
    <ul>
      <li>Topics, audiences, brand voice, and other inputs you provide to our generators</li>
      <li>Generated text, images, and videos</li>
      <li>Files you upload (images, videos) for use in posts</li>
      <li>Scheduling metadata (when, where, status of each post)</li>
    </ul>

    <h3>1.4 Usage data</h3>
    <ul>
      <li>API request logs (endpoint, timestamp, response status — no personal content)</li>
      <li>Aggregated usage metrics (number of posts generated, platforms used) for product improvement</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li><strong>Provide the Service</strong> — generate content, publish to your connected platforms, schedule posts</li>
      <li><strong>Authenticate</strong> you and keep your account secure</li>
      <li><strong>Process payments</strong> via Stripe and manage subscriptions</li>
      <li><strong>Send transactional emails</strong> (account verification, password resets, organization invitations) via SendGrid</li>
      <li><strong>Improve the Service</strong> through aggregated, de-identified analytics</li>
      <li><strong>Comply with legal obligations</strong> (tax records, fraud prevention, lawful requests)</li>
    </ul>

    <h2>3. Third-Party Services</h2>
    <p>To deliver the Service we share specific data with these vetted providers:</p>
    <ul>
      <li><strong>OpenAI</strong> — your generation prompts (topic, audience, tone). OpenAI does not train on data sent via API as of March 2023.</li>
      <li><strong>Stripe</strong> — billing email + subscription metadata for payment processing</li>
      <li><strong>SendGrid</strong> — your email address for transactional messages</li>
      <li><strong>MongoDB Atlas</strong> — encrypted database storage</li>
      <li><strong>Social platforms (LinkedIn / Meta / Google / TikTok / X)</strong> — only the content you explicitly publish, plus the OAuth handshake</li>
    </ul>
    <p>
      We do not sell your personal information or generated content to data brokers, advertisers, or AI training datasets.
    </p>

    <h2>4. Platform-Specific Disclosures</h2>

    <h3>4.1 Meta (Facebook + Instagram)</h3>
    <p>
      Our Meta integration uses the Instagram Graph API and Facebook Pages API in compliance with the
      <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer"> Meta Platform Terms</a>.
      We request only the permissions necessary to publish posts to Pages and Instagram Business accounts you administer
      (<code>pages_manage_posts</code>, <code>instagram_content_publish</code>, and supporting read scopes).
      We do not access your personal Facebook profile, friends list, or private messages.
    </p>

    <h3>4.2 LinkedIn</h3>
    <p>
      Our LinkedIn integration uses the <code>w_member_social</code> scope solely to publish posts you author to your
      own LinkedIn feed. We do not read your network, messages, or job activity.
    </p>

    <h3>4.3 Other platforms (YouTube, TikTok, X)</h3>
    <p>
      Each integration uses the minimum scopes required to publish the content you create. Tokens are stored
      encrypted and used only on your behalf.
    </p>

    <h2>5. Data Retention</h2>
    <ul>
      <li>Account and connection data: retained for as long as your account is active</li>
      <li>Generated content: retained until you delete it or close your account</li>
      <li>Backups: deleted within 30 days of account closure</li>
      <li>Billing records: retained 7 years to satisfy tax obligations</li>
    </ul>

    <h2>6. Your Rights</h2>
    <p>You can at any time:</p>
    <ul>
      <li><strong>Access</strong> your personal data via your account settings</li>
      <li><strong>Correct</strong> inaccurate data via account settings</li>
      <li><strong>Delete</strong> your account and all associated data — see our <Link to="/data-deletion">Data Deletion page</Link></li>
      <li><strong>Disconnect</strong> social platforms at any time from <Link to="/connections">/connections</Link>; we immediately revoke their use</li>
      <li><strong>Export</strong> your generated content as CSV from the Export page</li>
      <li><strong>Object to processing</strong> by emailing us at <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a></li>
    </ul>
    <p>
      EU/EEA, UK, and California residents have additional rights under GDPR / UK GDPR / CCPA including the right
      to lodge a complaint with a supervisory authority.
    </p>

    <h2>7. Security</h2>
    <p>
      We use industry-standard practices: bcrypt password hashing, HTTPS-only transport, encrypted database storage,
      JWT-based session tokens, role-based access control, and least-privilege OAuth scopes. No system is 100% secure
      — if you discover a vulnerability, please email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </p>

    <h2>8. Children's Privacy</h2>
    <p>
      The Service is not directed to children under 16. We do not knowingly collect personal information from children
      under 16. If you believe a child has provided us personal information, contact us and we will delete it.
    </p>

    <h2>9. Changes to This Policy</h2>
    <p>
      We may update this policy from time to time. Material changes will be communicated via email and a banner on
      the Service at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.
    </p>

    <h2>10. Contact</h2>
    <p>
      Questions, concerns, or requests:<br />
      <strong>{COMPANY}</strong><br />
      Email: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a><br />
      Support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
    </p>
  </LegalLayout>
);

// ============================================================
// Terms of Service
// ============================================================
export const TermsOfServicePage = () => (
  <LegalLayout title="Terms of Service">
    <p>
      These Terms of Service ("Terms") govern your access to and use of the {COMPANY} service available at
      <strong> {DOMAIN}</strong> (the "Service"). By creating an account or using the Service, you agree to these
      Terms. If you do not agree, do not use the Service.
    </p>

    <h2>1. Eligibility</h2>
    <p>
      You must be at least 16 years old and able to form a binding contract under applicable law. If you use the
      Service on behalf of a company, you represent that you have authority to bind that company to these Terms.
    </p>

    <h2>2. Account Registration</h2>
    <ul>
      <li>You must provide accurate, complete information during registration and keep it current</li>
      <li>You are responsible for maintaining the confidentiality of your password</li>
      <li>You are responsible for all activity that occurs under your account</li>
      <li>You must notify us immediately of any unauthorized use</li>
    </ul>

    <h2>3. Subscription & Billing</h2>
    <ul>
      <li>Some features require a paid subscription. Pricing is shown on the Subscription page.</li>
      <li>Subscriptions auto-renew at the end of each billing period unless cancelled.</li>
      <li>You can cancel anytime; access continues through the end of the paid period.</li>
      <li>All payments are processed by <strong>Stripe</strong>. We do not store your full card number.</li>
      <li>Prices may change with at least 30 days' notice for existing subscribers.</li>
      <li>Refunds are issued at our reasonable discretion; we generally do not refund partially-used billing periods.</li>
    </ul>

    <h2>4. Connected Social Platforms</h2>
    <p>
      The Service can publish content to social platforms you connect (LinkedIn, Facebook, Instagram, YouTube,
      TikTok, X). By connecting a platform you authorize us to publish posts on your behalf using the OAuth tokens you
      grant. You can disconnect at any time from <Link to="/connections">/connections</Link>.
    </p>
    <p>
      You are solely responsible for the content you publish. You must comply with each platform's own terms of
      service and community guidelines (e.g., LinkedIn Professional Community Policies, Meta Community Standards).
    </p>

    <h2>5. AI-Generated Content</h2>
    <ul>
      <li>The Service uses third-party AI models (OpenAI, Google, etc.) to generate text and images.</li>
      <li>AI output is provided "as is." It may contain inaccuracies, biases, or unintended phrasing.</li>
      <li><strong>You are responsible for reviewing all generated content before publishing.</strong></li>
      <li>You retain ownership of inputs you provide and outputs you publish, subject to the underlying AI model's terms.</li>
      <li>You may not publish content that violates intellectual property rights, defames others, or breaks any law.</li>
    </ul>

    <h2>6. Acceptable Use</h2>
    <p>You agree NOT to use the Service to:</p>
    <ul>
      <li>Generate or publish illegal, harmful, threatening, harassing, defamatory, or hateful content</li>
      <li>Spam, mass-publish low-quality content, or run automated bot networks</li>
      <li>Impersonate any person or entity</li>
      <li>Infringe trademarks, copyrights, or other intellectual property</li>
      <li>Generate misinformation, deepfakes, or content designed to deceive</li>
      <li>Reverse-engineer, scrape, or attempt to extract our source code, training data, or proprietary prompts</li>
      <li>Resell, sublicense, or white-label the Service without our written permission</li>
      <li>Use the Service to develop a competing product</li>
    </ul>
    <p>
      We may suspend or terminate accounts that violate these rules.
    </p>

    <h2>7. Intellectual Property</h2>
    <ul>
      <li>The Service, its software, design, trademarks, and the {COMPANY} name are owned by us.</li>
      <li>You retain ownership of content you create. By using the Service you grant us a worldwide, royalty-free, non-exclusive license to host, store, and process your content as needed to operate the Service.</li>
      <li>We do <strong>not</strong> use your content to train AI models or sell to third parties.</li>
    </ul>

    <h2>8. Termination</h2>
    <ul>
      <li>You can close your account at any time from settings or by emailing <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></li>
      <li>We may suspend or terminate accounts that violate these Terms, with or without notice</li>
      <li>Upon termination, your data is deleted per our <Link to="/privacy">Privacy Policy</Link> retention schedule</li>
    </ul>

    <h2>9. Disclaimers</h2>
    <p>
      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
      INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI-GENERATED OUTPUT WILL
      MEET YOUR EXPECTATIONS.
    </p>

    <h2>10. Limitation of Liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
      INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, OR USE,
      WHETHER IN CONTRACT, TORT, OR OTHERWISE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY. OUR TOTAL
      LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless {COMPANY} from any claim, loss, or damage arising from (a) your use
      of the Service, (b) content you publish, (c) your violation of these Terms, or (d) your violation of any
      third-party rights including intellectual property or platform terms.
    </p>

    <h2>12. Governing Law</h2>
    <p>
      These Terms are governed by the laws of the United States and the State of Delaware, without regard to
      conflict-of-law principles. Disputes shall be resolved exclusively in the state or federal courts located in
      Delaware, except that you may also bring small-claims actions in your local jurisdiction.
    </p>

    <h2>13. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be communicated via email and a banner on
      the Service at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.
    </p>

    <h2>14. Contact</h2>
    <p>
      <strong>{COMPANY}</strong><br />
      Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
    </p>
  </LegalLayout>
);

// ============================================================
// Data Deletion Page (Meta-required)
// ============================================================
export const DataDeletionPage = () => (
  <LegalLayout title="Data Deletion">
    <p>
      You have the right to delete your {COMPANY} account and any data we hold about you. This page describes how.
    </p>

    <h2>Option 1 — Self-service (recommended)</h2>
    <ol>
      <li>Sign in to your account at <a href="https://posttosocial.com/login">posttosocial.com/login</a></li>
      <li>Go to <strong>Settings → Account → Delete account</strong></li>
      <li>Confirm by entering your password</li>
      <li>Your account, generated content, organization memberships, and connected platform tokens are deleted immediately. Backups are purged within 30 days.</li>
    </ol>

    <h2>Option 2 — Disconnect a single platform only</h2>
    <p>
      If you only want to revoke our access to one social platform (e.g., remove Facebook but keep using {COMPANY}):
    </p>
    <ol>
      <li>Sign in and go to <Link to="/connections">/connections</Link></li>
      <li>Click <strong>Disconnect</strong> on the platform you want to remove</li>
      <li>The OAuth token is deleted immediately. The platform itself can also revoke it from their settings:
        <ul>
          <li><a href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noopener noreferrer">Facebook Business Tools settings</a></li>
          <li><a href="https://www.instagram.com/accounts/manage_access/" target="_blank" rel="noopener noreferrer">Instagram authorized apps</a></li>
          <li><a href="https://www.linkedin.com/psettings/permitted-services" target="_blank" rel="noopener noreferrer">LinkedIn permitted services</a></li>
          <li><a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">Google account permissions</a></li>
          <li><a href="https://www.tiktok.com/setting/connected-accounts" target="_blank" rel="noopener noreferrer">TikTok connected accounts</a></li>
          <li><a href="https://x.com/settings/connected_apps" target="_blank" rel="noopener noreferrer">X connected apps</a></li>
        </ul>
      </li>
    </ol>

    <h2>Option 3 — Email request</h2>
    <p>
      If you're unable to access your account, email <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> from
      the address associated with your account with the subject line <strong>"Delete my data"</strong>. We will:
    </p>
    <ul>
      <li>Verify your identity within 7 business days</li>
      <li>Delete your account and confirm via email</li>
      <li>Purge backups within 30 days of confirmation</li>
    </ul>

    <h2>What gets deleted</h2>
    <ul>
      <li>Your account record (email, name, hashed password, settings)</li>
      <li>All posts, blogs, ads, emails, and other content you generated</li>
      <li>All uploaded media (images, videos)</li>
      <li>All OAuth tokens for connected social platforms</li>
      <li>Organization memberships (if you owned an organization, ownership is transferred to another admin or the organization is deleted)</li>
      <li>Subscription record (Stripe billing records are retained per applicable tax law)</li>
    </ul>

    <h2>What we keep (and why)</h2>
    <ul>
      <li><strong>Billing records</strong> — retained for 7 years to satisfy tax and audit obligations</li>
      <li><strong>Aggregated, de-identified analytics</strong> — usage metrics that cannot identify you (e.g., "X posts generated by free-tier users in March")</li>
      <li><strong>Logs</strong> — server access logs for security investigations, retained 90 days, then permanently deleted</li>
    </ul>

    <h2>Meta-specific (Facebook + Instagram)</h2>
    <p>
      In compliance with the <a href="https://developers.facebook.com/docs/development/maintaining-your-app/data-deletion-callback/" target="_blank" rel="noopener noreferrer">Meta Data Deletion Callback</a> requirement,
      we honor signed deletion requests sent by Meta when a user removes our app from their Facebook account.
      The callback URL is <code>https://posttosocial.com/api/meta/data-deletion-callback</code>. Upon receiving such a
      request, we delete the associated user's connected platform tokens and confirmation code is returned to Meta
      within 24 hours.
    </p>
    <p>
      To trigger this from Meta's side: open Facebook → Settings → Apps and Websites → click {COMPANY} → Remove.
      Meta will send us a deletion request automatically.
    </p>

    <h2>Questions</h2>
    <p>
      Email <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> for any data-deletion related questions.
      Typical response time: 1 business day.
    </p>
  </LegalLayout>
);

export default PrivacyPolicyPage;
